import { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Switch,
  Popconfirm,
  message,
  Card,
  Modal,
  Form,
  Input,
  InputNumber,
  Tooltip,
  Row,
  Col,
  Statistic,
  Avatar,
  Typography,
  Upload,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PictureOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  InboxOutlined,
  OrderedListOutlined,
} from "@ant-design/icons";
import api from "../../api/axios";

const { Text } = Typography;

// مطابق لـ path('api/custom_services/', include('custom_services.urls')) في urls.py الرئيسي
const API_BASE = "/custom_services/admin/onboarding/";

const CLOUD_NAME = "dftpzis0y";
const UPLOAD_PRESET = "ml_default";

export default function Onboarding() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const fetchSlides = async () => {
    setLoading(true);
    try {
      const res = await api.get(API_BASE);
      const data = Array.isArray(res.data) ? res.data : res.data.results ?? [];
      setSlides(data);
    } catch {
      message.error("فشل تحميل سلايدز الأونبوردينج");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlides();
  }, []);

  const handleImageUpload = async ({ file }) => {
    if (file.size > 5 * 1024 * 1024) {
      message.error("حجم الصورة يجب أن يكون أقل من 5MB");
      return false;
    }

    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("upload_preset", UPLOAD_PRESET);
      fd.append("folder", "onboarding");

      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
        { method: "POST", body: fd }
      );

      const data = await res.json();
      if (data.secure_url) {
        form.setFieldValue("image", data.secure_url);
        setPreviewImage(data.secure_url);
        message.success("تم رفع الصورة بنجاح");
      } else {
        message.error("فشل رفع الصورة، حاول مرة أخرى");
      }
    } catch {
      message.error("حدث خطأ أثناء رفع الصورة");
    } finally {
      setUploadingImage(false);
    }
    return false;
  };

  const openModal = (slide = null) => {
    setEditingSlide(slide);
    setPreviewImage(slide?.image ?? null);
    if (slide) {
      form.setFieldsValue({
        title: slide.title,
        description: slide.description,
        image: slide.image,
        order: slide.order,
        is_active: slide.is_active,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ is_active: true, order: slides.length });
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (editingSlide) {
        await api.patch(`${API_BASE}${editingSlide.id}/`, values);
        message.success("تم تحديث السلايد بنجاح");
      } else {
        await api.post(API_BASE, values);
        message.success("تم إضافة السلايد بنجاح");
      }
      setModalOpen(false);
      fetchSlides();
    } catch (e) {
      if (e?.errorFields) return;
      message.error("فشل الحفظ، حاول مرة أخرى");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`${API_BASE}${id}/`);
      message.success("تم حذف السلايد");
      fetchSlides();
    } catch {
      message.error("فشل الحذف");
    }
  };

  const handleToggleActive = async (id, current) => {
    try {
      await api.patch(`${API_BASE}${id}/`, { is_active: !current });
      message.success(current ? "تم تعطيل السلايد" : "تم تفعيل السلايد");
      fetchSlides();
    } catch {
      message.error("فشل تغيير الحالة");
    }
  };

  const activeCount = slides.filter((s) => s.is_active).length;
  const inactiveCount = slides.filter((s) => !s.is_active).length;

  const sortedSlides = [...slides].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );

  const columns = [
    {
      title: "الترتيب",
      dataIndex: "order",
      key: "order",
      width: 90,
      render: (v) => (
        <Tag color="default" style={{ borderRadius: 6, fontWeight: 700 }}>
          {v}
        </Tag>
      ),
    },
    {
      title: "السلايد",
      key: "title",
      render: (_, r) => (
        <Space>
          {r.image ? (
            <Avatar
              size={44}
              src={r.image}
              shape="square"
              style={{ borderRadius: 8, flexShrink: 0 }}
            />
          ) : (
            <Avatar
              size={44}
              icon={<PictureOutlined />}
              shape="square"
              style={{ background: "#e07b1a", borderRadius: 8, flexShrink: 0 }}
            />
          )}
          <div>
            <div style={{ fontWeight: 600, color: "#1a1a2e" }}>{r.title}</div>
            <Text
              type="secondary"
              ellipsis
              style={{ fontSize: 12, maxWidth: 320, display: "block" }}
            >
              {r.description}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "الحالة",
      key: "is_active",
      render: (_, r) => (
        <Switch
          checked={r.is_active}
          checkedChildren="نشط"
          unCheckedChildren="معطل"
          onChange={() => handleToggleActive(r.id, r.is_active)}
          style={r.is_active ? { background: "#52c41a" } : {}}
        />
      ),
    },
    {
      title: "الإجراءات",
      key: "actions",
      fixed: "left",
      render: (_, r) => (
        <Space size="small">
          <Tooltip title="تعديل">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openModal(r)}
              style={{ color: "#e07b1a" }}
            />
          </Tooltip>
          <Popconfirm
            title="حذف السلايد؟"
            description="سيتم حذف السلايد نهائياً."
            onConfirm={() => handleDelete(r.id)}
            okText="حذف"
            cancelText="إلغاء"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="حذف">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                style={{ color: "#ff4d4f" }}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ fontFamily: "'Cairo', sans-serif" }}>
      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {[
          {
            title: "إجمالي السلايدز",
            value: slides.length,
            icon: <OrderedListOutlined />,
            color: "#e07b1a",
            bg: "#fff7e6",
          },
          {
            title: "سلايدز نشطة",
            value: activeCount,
            icon: <CheckCircleOutlined />,
            color: "#52c41a",
            bg: "#f6ffed",
          },
          {
            title: "سلايدز معطلة",
            value: inactiveCount,
            icon: <CloseCircleOutlined />,
            color: "#ff4d4f",
            bg: "#fff2f0",
          },
        ].map((s, i) => (
          <Col xs={24} sm={8} key={i}>
            <Card
              bordered={false}
              style={{ borderRadius: 12, background: s.bg }}
              bodyStyle={{ padding: "16px 20px" }}
            >
              <Statistic
                title={
                  <span style={{ color: "#666", fontSize: 13 }}>{s.title}</span>
                }
                value={s.value}
                valueStyle={{ color: s.color, fontWeight: 700, fontSize: 28 }}
                prefix={
                  <span style={{ color: s.color, marginLeft: 6 }}>
                    {s.icon}
                  </span>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Table Card */}
      <Card
        bordered={false}
        style={{ borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
        bodyStyle={{ padding: 0 }}
      >
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #f0f0f0",
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: "#0f1f1a",
            }}
          >
            شاشات الأونبوردينج
          </h2>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
            style={{ background: "#e07b1a", borderColor: "#e07b1a" }}
          >
            إضافة سلايد
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={sortedSlides}
          loading={loading}
          rowKey="id"
          scroll={{ x: 700 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showTotal: (total) => `${total} سلايد`,
          }}
          style={{ padding: "0 8px" }}
        />
      </Card>

      {/* Modal */}
      <Modal
        title={
          <span style={{ fontFamily: "'Cairo', sans-serif", fontWeight: 700 }}>
            {editingSlide ? "تعديل السلايد" : "إضافة سلايد جديد"}
          </span>
        }
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => {
          setModalOpen(false);
          setPreviewImage(null);
        }}
        okText={editingSlide ? "حفظ التعديلات" : "إضافة"}
        cancelText="إلغاء"
        confirmLoading={saving}
        okButtonProps={{
          style: { background: "#e07b1a", borderColor: "#e07b1a" },
          disabled: uploadingImage,
        }}
        style={{ direction: "rtl", fontFamily: "'Cairo', sans-serif" }}
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="title"
            label="عنوان السلايد"
            rules={[{ required: true, message: "الرجاء إدخال العنوان" }]}
          >
            <Input placeholder="مثال: اطلب أي خدمة بسهولة" />
          </Form.Item>

          <Form.Item
            name="description"
            label="نص السلايد"
            rules={[{ required: true, message: "الرجاء إدخال نص السلايد" }]}
          >
            <Input.TextArea rows={3} placeholder="وصف قصير يشرح الميزة..." />
          </Form.Item>

          {/* hidden URL field */}
          <Form.Item
            name="image"
            style={{ display: "none" }}
            rules={[{ required: true, message: "الصورة مطلوبة" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="صورة السلايد" required>
            <Upload
              accept="image/*"
              showUploadList={false}
              customRequest={handleImageUpload}
              disabled={uploadingImage}
            >
              <div
                style={{
                  border: `1px dashed ${
                    uploadingImage ? "#e07b1a" : "#d9d9d9"
                  }`,
                  borderRadius: 10,
                  padding: previewImage ? 8 : 20,
                  textAlign: "center",
                  cursor: uploadingImage ? "not-allowed" : "pointer",
                  background: "#fafafa",
                  transition: "border-color 0.25s",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  if (!uploadingImage)
                    e.currentTarget.style.borderColor = "#e07b1a";
                }}
                onMouseLeave={(e) => {
                  if (!uploadingImage)
                    e.currentTarget.style.borderColor = "#d9d9d9";
                }}
              >
                {uploadingImage ? (
                  <Space direction="vertical" size={6} style={{ padding: 12 }}>
                    <LoadingOutlined
                      style={{ fontSize: 26, color: "#e07b1a" }}
                    />
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      جاري رفع الصورة...
                    </Text>
                  </Space>
                ) : previewImage ? (
                  <Space
                    direction="vertical"
                    size={8}
                    style={{ width: "100%" }}
                  >
                    <img
                      src={previewImage}
                      alt="preview"
                      style={{
                        width: "100%",
                        maxHeight: 160,
                        objectFit: "cover",
                        borderRadius: 8,
                        display: "block",
                      }}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      اضغط لتغيير الصورة
                    </Text>
                  </Space>
                ) : (
                  <Space direction="vertical" size={4}>
                    <InboxOutlined style={{ fontSize: 32, color: "#bbb" }} />
                    <Text style={{ fontSize: 14, color: "#555" }}>
                      اضغط أو اسحب الصورة هنا
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      PNG، JPG، WEBP — الحجم الأقصى 5MB
                    </Text>
                  </Space>
                )}
              </div>
            </Upload>
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="order"
                label="ترتيب الظهور"
                tooltip="الرقم الأصغر يظهر أولاً"
                rules={[{ required: true, message: "الرجاء إدخال الترتيب" }]}
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_active"
                label="الحالة"
                valuePropName="checked"
              >
                <Switch checkedChildren="نشط" unCheckedChildren="معطل" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
